/**
 * Data tab — per-table row counts, on-disk storage breakdown, plus
 * the two destructive utilities (export JSON, wipe all). Wipe is
 * two-tap confirm since `window.confirm()` is a no-op in Tauri
 * WKWebView and we'd rather no one trigger a silent data nuke by
 * accident.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Download,
  Loader2,
  Mic,
  RefreshCw,
  Sparkles,
  Trash2,
} from "lucide-react";

import {
  devBackfillSummaries,
  devExportData,
  devGetStorageStats,
  devWipeVoiceData,
} from "@/hooks/useDevDiagnostics";
import { setDevRecordScreenOpen } from "@/hooks/useDevRecordScreen";
import { clearProfile } from "@/onboarding/profileStorage";
import type { DevStorageStats } from "@/types/devMode";

import { formatBytes } from "./devFormatters";

export function DataTab() {
  const [stats, setStats] = useState<DevStorageStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [lastExport, setLastExport] = useState<string | null>(null);
  const [wipeArmed, setWipeArmed] = useState(false);
  const [lastBackfill, setLastBackfill] = useState<number | null>(null);
  const wipeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const payload = await devGetStorageStats();
      setStats(payload);
    } catch (err) {
      setError(String(err));
    }
  }, []);

  useEffect(() => {
    void refresh();
    return () => {
      if (wipeTimerRef.current) clearTimeout(wipeTimerRef.current);
    };
  }, [refresh]);

  const handleExport = useCallback(async () => {
    setBusy("export");
    setError(null);
    try {
      const result = await devExportData();
      setLastExport(result.path);
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(null);
    }
  }, []);

  const handleBackfill = useCallback(async () => {
    setBusy("backfill");
    setError(null);
    try {
      const result = await devBackfillSummaries();
      setLastBackfill(result.queued);
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(null);
    }
  }, []);

  const handleWipe = useCallback(async () => {
    if (!wipeArmed) {
      setWipeArmed(true);
      if (wipeTimerRef.current) clearTimeout(wipeTimerRef.current);
      wipeTimerRef.current = setTimeout(() => setWipeArmed(false), 4000);
      return;
    }
    if (wipeTimerRef.current) clearTimeout(wipeTimerRef.current);
    setWipeArmed(false);
    setBusy("wipe");
    setError(null);
    try {
      await devWipeVoiceData();
      // Also clear the onboarding profile in localStorage so the app
      // drops back into the enrollment flow on next launch. Without
      // this the user sees "no voiceprint stored yet" with no way to
      // re-enroll — App.tsx gates Me/Conversation behind a stored
      // profile, not behind a stored voiceprint.
      clearProfile();
      // Force reload so App.tsx re-evaluates the profile check and
      // routes to onboarding immediately. Without the reload, the
      // user would see the stale authenticated UI until next
      // cold-start.
      window.location.reload();
    } catch (err) {
      setError(String(err));
      setBusy(null);
    }
  }, [wipeArmed]);

  if (error) {
    return <p className="p-4 text-sm text-red-400">Failed: {error}</p>;
  }

  if (!stats) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-white/40">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading stats…
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-[0.2em] text-white/40">
          On-device storage
        </p>
        <button
          onClick={refresh}
          className="flex items-center gap-1 text-[11px] text-white/50 active:opacity-60"
        >
          <RefreshCw className="h-3 w-3" />
          refresh
        </button>
      </div>

      <section className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
        <p className="mb-2 text-[11px] uppercase tracking-[0.15em] text-white/40">
          Disk
        </p>
        <StorageRow label="SQLite DB" bytes={stats.dbSizeBytes} />
        <StorageRow label="Sessions audio" bytes={stats.sessionsDirSizeBytes} />
        <StorageRow
          label="Provisional snippets"
          bytes={stats.samplesDirSizeBytes}
        />
        <StorageRow label="Enrollment audio" bytes={stats.rawDirSizeBytes} />
        <div className="mt-1.5 flex items-center justify-between border-t border-white/10 pt-1.5">
          <span className="text-[12px] text-white/60">Total</span>
          <span className="font-mono text-[12px] font-semibold text-white">
            {formatBytes(
              stats.dbSizeBytes +
                stats.sessionsDirSizeBytes +
                stats.samplesDirSizeBytes +
                stats.rawDirSizeBytes
            )}
          </span>
        </div>
      </section>

      <section className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
        <p className="mb-2 text-[11px] uppercase tracking-[0.15em] text-white/40">
          Row counts
        </p>
        <RowCount label="voiceprints" value={stats.voiceprintCount} />
        <RowCount label="voiceprint_samples" value={stats.sampleCount} />
        <RowCount label="sessions" value={stats.sessionCount} />
        <RowCount label="turns" value={stats.turnCount} />
        <RowCount label="interactions" value={stats.interactionCount} />
        <RowCount
          label="provisional_speakers"
          value={stats.provisionalCount}
        />
        <RowCount label="adaptation_events" value={stats.adaptationCount} />
      </section>

      <section className="flex flex-col gap-2">
        <button
          type="button"
          onClick={() => setDevRecordScreenOpen(true)}
          className="flex items-center justify-center gap-2 rounded-lg border border-yellow-400/40 bg-yellow-400/10 px-3 py-2.5 text-[13px] font-medium text-yellow-100 active:opacity-70"
        >
          <Mic className="h-4 w-4" />
          Open record controls
        </button>

        <button
          type="button"
          onClick={handleBackfill}
          disabled={busy !== null}
          className="flex items-center justify-center gap-2 rounded-lg border border-violet-400/40 bg-violet-500/10 px-3 py-2.5 text-[13px] font-medium text-violet-100 active:opacity-70 disabled:opacity-50"
        >
          {busy === "backfill" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4" />
          )}
          Regenerate missing summaries
        </button>
        {lastBackfill !== null && (
          <p className="text-[11px] text-violet-100/60">
            Queued {lastBackfill}{" "}
            {lastBackfill === 1 ? "interaction" : "interactions"}. Summaries
            populate in the background — reopen the contact in a minute.
          </p>
        )}

        <button
          type="button"
          onClick={handleExport}
          disabled={busy !== null}
          className="flex items-center justify-center gap-2 rounded-lg border border-white/15 bg-white/[0.04] px-3 py-2.5 text-[13px] font-medium text-white active:opacity-70 disabled:opacity-50"
        >
          {busy === "export" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          Export diagnostics JSON
        </button>
        {lastExport && (
          <div className="flex flex-col gap-1 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3">
            <p className="text-[12px] font-medium text-emerald-200">
              Saved.
            </p>
            <p className="text-[11px] leading-relaxed text-emerald-100/70">
              Open the Files app → On My iPhone → Meridian → voice/ to find
              the JSON. AirDrop or share from there.
            </p>
            <p className="break-all pt-1 font-mono text-[10px] text-white/30">
              {lastExport}
            </p>
          </div>
        )}

        <button
          type="button"
          onClick={handleWipe}
          disabled={busy !== null}
          className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-[13px] font-medium active:opacity-70 disabled:opacity-50 ${
            wipeArmed
              ? "border-red-500/70 bg-red-500/15 text-red-200"
              : "border-white/15 bg-white/[0.04] text-white/70"
          }`}
        >
          {busy === "wipe" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
          {wipeArmed ? "Tap again to wipe everything" : "Wipe all voice data"}
        </button>
        <p className="text-[11px] text-white/40">
          Nukes voiceprints, samples, sessions, turns, interactions, adaptation
          history, every on-disk audio artifact, AND your onboarding profile —
          the app reloads straight into enrollment. Apple Contacts are not
          touched. Two-tap confirm.
        </p>
      </section>
    </div>
  );
}

function StorageRow({ label, bytes }: { label: string; bytes: number }) {
  return (
    <div className="flex items-center justify-between py-0.5 text-[12px]">
      <span className="text-white/60">{label}</span>
      <span className="font-mono text-white/80">{formatBytes(bytes)}</span>
    </div>
  );
}

function RowCount({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-center justify-between py-0.5 font-mono text-[12px]">
      <span className="text-white/60">{label}</span>
      <span className="text-white/80">{value}</span>
    </div>
  );
}
